import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CartItem from '../components/CartItem';
import { Gift } from 'lucide-react';

const Cart = () => {
  const { cart, giftPacking, toggleGiftPacking } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  // State to hold IDs of checked items for checkout
  const [selectedItems, setSelectedItems] = useState([]);

  // Initialize checkboxes based on whether user clicked "Buy Now" or standard cart link
  useEffect(() => {
    const targetProductId = location.state?.targetProductId;

    if (targetProductId && cart.some(item => item._id === targetProductId)) {
      // "Buy Now" mode: Only check the targeted product reference item
      setSelectedItems([targetProductId]);
    } else {
      // Standard mode: Check all items by default
      setSelectedItems(cart.map(item => item._id));
    }
  }, [location.state, cart]);

  // Toggle single product checkbox
  const handleToggleSelect = (id) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
    );
  };

  // Toggle master select-all checkbox
  const handleToggleSelectAll = () => {
    if (selectedItems.length === cart.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(cart.map(item => item._id));
    }
  };

  // Calculate Order Summary ONLY using checked items
  const activeCartItems = cart.filter(item => selectedItems.includes(item._id));
  
  const activeCartTotal = Math.round(activeCartItems.reduce(
    (total, item) => total + item.price * (item.quantity || 1), 
    0
  ));

  const shippingPrice = activeCartTotal === 0 ? 0 : (activeCartTotal > 100 ? 0 : 10);
  const taxPrice = Math.round(activeCartTotal * 0.18);
  const giftPackingPrice = giftPacking && activeCartTotal > 0 ? 50 : 0;
  const totalPrice = Math.round(activeCartTotal + shippingPrice + taxPrice + giftPackingPrice);

  const handleProceedToCheckout = () => {
    if (selectedItems.length === 0) {
      alert("Please select at least one item to proceed to checkout.");
      return;
    }
    // Forwards checked product configurations explicitly to the payment route
    navigate('/checkout', { state: { checkoutItems: selectedItems } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Shopping Cart</h1>

        {cart.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🛒</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Add some products to get started!</p>
            <button
              onClick={() => navigate('/store')}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              
              {/* Modern Grid Action Header Bar */}
              <div className="flex items-center justify-between bg-white px-4 py-2.5 rounded-xl border border-slate-200 shadow-sm text-sm">
                <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-600">
                  <input 
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500 accent-purple-600"
                    checked={cart.length > 0 && selectedItems.length === cart.length}
                    onChange={handleToggleSelectAll}
                  />
                  Select All Items ({cart.length})
                </label>
                <span className="text-xs text-slate-400 font-medium">
                  Selected: {selectedItems.length}
                </span>
              </div>

              {/* Product Checklist Map Wrapper */}
              <div className="space-y-3">
                {cart.map(item => (
                  <div key={item._id} className="flex items-center gap-3 bg-white pr-4 rounded-2xl shadow-sm border border-slate-100 transition hover:border-slate-200">
                    <div className="pl-4">
                      <input 
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500 accent-purple-600 cursor-pointer"
                        checked={selectedItems.includes(item._id)}
                        onChange={() => handleToggleSelect(item._id)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CartItem item={item} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Sticky Summary Block */}
            <div className="lg:col-span-1">
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-6 sticky top-24">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Order Summary</h2>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal ({selectedItems.length} items):</span>
                    <span className="font-medium">₹{activeCartTotal}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping:</span>
                    <span className="font-medium">₹{shippingPrice}</span>
                  </div>
                  {activeCartTotal > 100 && (
                    <p className="text-sm text-green-600">🎉 Free shipping applied!</p>
                  )}
                  
                  {/* Gift Packing Option */}
                  <div 
                    className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                      giftPacking ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                    }`}
                    onClick={toggleGiftPacking}
                  >
                    <div className="flex items-center gap-2">
                      <Gift size={20} className={giftPacking ? 'text-purple-600' : 'text-gray-400'} />
                      <div>
                        <p className="font-medium text-gray-800 text-sm">Gift Packing</p>
                        <p className="text-xs text-gray-500">Add special packaging</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-purple-600">+₹50</p>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        giftPacking ? 'bg-purple-600 border-purple-600' : 'border-gray-300'
                      }`}>
                        {giftPacking && <div className="w-2 h-2 bg-white rounded-full" />}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-gray-600">
                    <span>Tax (18%):</span>
                    <span className="font-medium">₹{taxPrice}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between text-xl font-bold text-gray-800">
                    <span>Total:</span>
                    <span className="text-purple-600">₹{totalPrice}</span>
                  </div>
                </div>
                <button
                  onClick={handleProceedToCheckout}
                  disabled={selectedItems.length === 0}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-xl hover:shadow-lg transition-all font-medium disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed"
                >
                  Proceed to Checkout ({selectedItems.length})
                </button>
                <button
                  onClick={() => navigate('/store')}
                  className="w-full mt-3 bg-gray-100 text-gray-700 py-3 rounded-xl hover:bg-gray-200 transition-all font-medium"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Cart;